import debounce from 'lodash/debounce';
import { GoogleMaps } from './GoogleMaps';
import { Sidebar } from '../modules/Sidebar';
import { syncAccordionState } from '../modules/accordion';

class WhereToBuySidebarPanel {
    constructor(root) {
        if (!root) {
            return;
        }

        this.root = root;
        this.loader = root.querySelector('.loader');
        this.content = root.querySelector('[data-where-to-buy-content]');
        this.checkboxes = root.querySelectorAll('input[name="where_to_buy_shop_type"]');
        this.page = 1;
        this.phrase = '';
        this.onlineShopsPerPage = 4;
        this.activeType = Array.from(this.checkboxes).find(item => item.checked)?.value || 'shop';
        this.mapInstance = null;
        this.mapWrapper = null;

        this.fetchDebounced = debounce((...args) => this.fetch(...args), 300);
        this.bindEvents();
    }

    bindEvents() {
        this.checkboxes.forEach(checkbox => {
            checkbox.addEventListener('change', this.onTabChange.bind(this));
        });

        this.root.addEventListener('input', this.onPhraseInput.bind(this));
        this.root.addEventListener('submit', this.onFormSubmit.bind(this));
        this.root.addEventListener('click', this.onClick.bind(this));
        window.addEventListener('accordion:change', this.onAccordionChange.bind(this));

        this.initMap();
        syncAccordionState(this.content);
    }

    onTabChange(event) {
        this.activeType = event.target.value;
        this.phrase = '';
        this.page = 1;
        this.destroyMap();
        this.fetch(true, this.content);
    }

    onPhraseInput(event) {
        const form = event.target.closest('[js-where-to-buy-phrase-form]');

        if (!form || !this.root.contains(form)) {
            return;
        }

        this.phrase = event.target.value;
        this.page = 1;

        if (this.activeType === 'shop') {
            this.fetchDebounced(true, this.getShopListOutput(), true, true);
            return;
        }

        this.fetchDebounced(true, this.content);
    }

    onFormSubmit(event) {
        const form = event.target.closest('[js-where-to-buy-phrase-form]');

        if (!form || !this.root.contains(form)) {
            return;
        }

        event.preventDefault();
    }

    async onClick(event) {
        const shopPaginationButton = event.target.closest(
            '.js-where-to-buy-shop-pagination-button'
        );
        const onlinePaginationButton = event.target.closest(
            '.js-where-to-buy-online-shop-pagination-button'
        );

        if (!shopPaginationButton && !onlinePaginationButton) {
            return;
        }

        const paginationElement = event.target.closest('[js-pagination]');
        const buttonWrapper = event.target.closest('[js-pagination-button-wrapper]');

        if (!paginationElement || !buttonWrapper) {
            return;
        }

        this.page += 1;

        const listSelector = shopPaginationButton
            ? '[js-shops-list]'
            : '[js-online-shops-list]';
        const listOutput = this.root.querySelector(listSelector);

        await this.fetch(false, listOutput);

        const current = paginationElement.querySelector('[js-current]');
        const progressBar = paginationElement.querySelector('[js-progress-bar]');
        const totalElement = parseInt(paginationElement.getAttribute('data-total'), 10);
        const itemSelector = shopPaginationButton
            ? '[js-shop-accordion-element]'
            : '[js-online-shop-box]';
        const elements = this.root.querySelectorAll(itemSelector);

        if (current) {
            current.textContent = elements.length;
        }

        if (progressBar && totalElement > 0) {
            progressBar.style.width = `${(elements.length / totalElement) * 100}%`;
        }

        paginationElement.setAttribute('data-page', String(this.page));

        if (this.page === parseInt(paginationElement.getAttribute('data-max-pages'), 10)) {
            buttonWrapper.classList.add('hidden');
        } else {
            buttonWrapper.classList.remove('hidden');
        }
    }

    onAccordionChange(event) {
        const { element, opened } = event.detail;

        if (
            !element ||
            !this.root.contains(element) ||
            this.activeType !== 'shop' ||
            !this.mapWrapper ||
            !this.mapInstance
        ) {
            return;
        }

        if (!opened) {
            this.mapInstance.clearActiveMarker();
            return;
        }

        const accordionId = element.dataset.id;

        if (!accordionId || !this.mapInstance.markers.length) {
            return;
        }

        this.mapInstance.goToMarker(accordionId);
    }

    getShopListOutput() {
        return this.root.querySelector('[data-where-to-buy-shop-list]');
    }

    getInitialShopAccordionId(scope = this.content) {
        const activeAccordion = scope.querySelector('[js-shop-accordion-element].-active');

        if (activeAccordion?.dataset.id) {
            return activeAccordion.dataset.id;
        }

        const firstAccordion = scope.querySelector('[js-shop-accordion-element]');

        return firstAccordion?.dataset.id ?? null;
    }

    destroyMap() {
        this.mapInstance = null;
        this.mapWrapper = null;
    }

    initMap(scope = this.content) {
        this.mapWrapper = scope.querySelector('[js-map-wrapper]');

        if (!this.mapWrapper) {
            return;
        }

        const points = JSON.parse(this.mapWrapper.getAttribute('data-point') || '[]');

        if (!Array.isArray(points) || points.length === 0) {
            return;
        }

        this.mapInstance = new GoogleMaps(this.mapWrapper);
        this.mapInstance.initMap(this.getInitialShopAccordionId(scope));
    }

    async fetch(
        replaceContent = true,
        htmlOutput = this.content,
        focusInput = false,
        onlyList = false
    ) {
        if (!htmlOutput) {
            return;
        }

        try {
            if (this.loader) {
                this.loader.classList.add('active');
            }

            const formData = new FormData();
            formData.append('action', 'mda_where_to_buy_sidebar_content');
            formData.append('type', this.activeType);
            formData.append('phrase', this.phrase);
            formData.append('page', String(this.page));
            formData.append('onlineShopsPerPage', String(this.onlineShopsPerPage));

            if (onlyList) {
                formData.append('onlyList', '1');
            }

            const response = await fetch(ajax.url, {
                method: 'POST',
                body: formData,
                credentials: 'same-origin',
            });

            if (!response.ok) {
                return;
            }

            const result = await response.json();

            if (!result?.success || typeof result?.data?.html !== 'string') {
                return;
            }

            if (replaceContent) {
                htmlOutput.innerHTML = result.data.html;
            } else {
                htmlOutput.innerHTML += result.data.html;
            }

            syncAccordionState(htmlOutput);

            if (this.activeType === 'shop') {
                this.mapWrapper = this.root.querySelector('[js-map-wrapper]');

                if (this.mapWrapper && this.page === 1 && !onlyList) {
                    this.initMap(this.root);
                } else if (this.mapWrapper && this.mapInstance) {
                    const first = this.getInitialShopAccordionId(htmlOutput);

                    if (onlyList && first) {
                        this.mapInstance.goToMarker(first);
                    } else if (onlyList) {
                        this.mapInstance.clearActiveMarker();
                    }
                }
            } else if (this.activeType === 'nowodvorski-shop') {
                this.destroyMap();
                this.initMap(htmlOutput);
            }

            if (focusInput) {
                const input = this.root.querySelector('[name="phrase"]');

                if (input) {
                    input.focus();
                    input.setSelectionRange(input.value.length, input.value.length);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            if (this.loader) {
                this.loader.classList.remove('active');
            }
        }
    }
}

class WhereToBuySidebar {
    constructor() {
        if (typeof ajax === 'undefined') {
            return;
        }

        this.sidebar = new Sidebar({
            overlayClassNames: ['bg-grey-900/60'],
        });
        this.contentHost = document.createElement('div');
        this.contentHost.className = 'h-full w-full max-w-[720px]';
        this.sidebar.content.appendChild(this.contentHost);
        this.isLoading = false;
        this.panel = null;

        document.addEventListener('click', this.onDocumentClick.bind(this), true);
    }

    async onDocumentClick(event) {
        const trigger = event.target.closest('[data-open-where-to-buy-sidebar]');

        if (!trigger) {
            return;
        }

        event.preventDefault();
        await this.openSidebar();
    }

    async openSidebar() {
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.panel = null;
        this.contentHost.innerHTML =
            '<div class="flex h-full w-full max-w-[720px] items-center justify-center bg-background-primary"><p class="text-regular text-text-secondary">...</p></div>';

        try {
            const html = await this.fetchSidebarHtml();

            if (!html) {
                return;
            }

            this.contentHost.innerHTML = html;
            this.panel = new WhereToBuySidebarPanel(
                this.contentHost.querySelector('[data-where-to-buy-sidebar]')
            );
            this.sidebar.open();
        } finally {
            this.isLoading = false;
        }
    }

    async fetchSidebarHtml() {
        const formData = new FormData();
        formData.append('action', 'mda_where_to_buy_sidebar');
        formData.append('nonce', ajax.nonce);

        const response = await fetch(ajax.url, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
        });

        if (!response.ok) {
            return null;
        }

        const result = await response.json();

        if (!result?.success || typeof result?.data?.html !== 'string') {
            return null;
        }

        return result.data.html;
    }
}

new WhereToBuySidebar();
